import {
  inferSectionMode,
  isKeptBenchmarkSection,
} from 'pages/private/owner/Workouts/forms/workoutBuilder/inferSectionMode';
import {
  extractMovementScoringPayload,
  hasMovementScoring,
} from 'pages/private/owner/Workouts/forms/workoutBuilder/scoringFormHelpers';
import type {
  WorkoutFormValues,
  WorkoutSectionData,
  WorkoutTemplateFormValues,
} from 'pages/private/owner/Workouts/forms/workoutBuilder/types';
import type { WorkoutTemplatePreviewData } from 'pages/private/owner/Workouts/Library/components/WorkoutLibrary/WorkoutTemplates/components/ModalWorkoutTemplatePreview/types';
import type {
  WorkoutCreate,
  WorkoutRetrieve,
  WorkoutTemplateRetrieve,
  WorkoutTemplateWrite,
  WorkoutUpdate,
} from 'types';
import { DistanceUnitEnum, ScoreTypeEnum, WeightUnitEnum } from 'types/enums';
import { getAlphabetLetterByIndex } from 'utils/functions';
import { assignmentTargetFields, type AssignmentTarget } from 'utils/workout.helpers';
import { v4 as uuidv4 } from 'uuid';

/**
 * Build WorkoutTemplatePreviewData for the preview modal from API template response.
 * Section score movement-backed section-level movements render via their (deterministic) name like
 * any other library movement — no special preview shape.
 */
export function buildPreviewDataFromTemplate(template: WorkoutTemplateRetrieve): WorkoutTemplatePreviewData {
  return {
    workoutTemplateId: template.workoutTemplateId,
    name: template.name,
    athleteNotes: template.athleteNotes,
    coachNotes: template.coachNotes,
    sections: template.sections.map(section => ({
      name: section.name,
      workoutInstructions: section.workoutInstructions,
      scoreEntireSection: inferSectionMode(section) === 'section_level',
      movements: section.movements.map(m => ({
        workoutMovement: {
          workoutMovementId: m.workoutMovement?.workoutMovementId ?? m.workoutMovementId ?? undefined,
          name: m.workoutMovement?.name ?? '',
          movementType: m.workoutMovement?.movementType,
        },
        scoreType: m.scoreType as ScoreTypeEnum,
      })),
    })),
  };
}

/**
 * Map a WorkoutTemplateRetrieve to WorkoutTemplateFormValues for prefilling the CreateWorkout form.
 * Section-level sections (single section-score-movement-backed movement) clear that movementId so
 * the assigned workout provisions its own section score movement — preventing template/workout
 * coupling through a shared FK.
 */
export function mapTemplateToFormValues(template: WorkoutTemplateRetrieve): WorkoutTemplateFormValues {
  return {
    name: template.name,
    athleteNotes: template.athleteNotes ?? '',
    coachNotes: template.coachNotes ?? '',
    sections: template.sections.map((sec, i): WorkoutSectionData => {
      const isSectionLevel = inferSectionMode(sec) === 'section_level';
      const kept = isKeptBenchmarkSection(sec);
      return {
        id: sec.workoutSectionTemplateId,
        letter: getAlphabetLetterByIndex(i),
        name: sec.name,
        description: sec.workoutInstructions ?? '',
        scoreEntireSection: isSectionLevel,
        saveAsBenchmark: isSectionLevel ? kept : true,
        levelScaled: sec.levelScaled,
        levelRx: sec.levelRx,
        levelRxPlus: sec.levelRxPlus,
        movements: (sec.movements ?? []).map((m, mi) => ({
          id: m.workoutSectionMovementTemplateId,
          movementId:
            isSectionLevel && mi === 0
              ? kept
                ? m.workoutMovement?.workoutMovementId ?? m.workoutMovementId ?? ''
                : ''
              : m.workoutMovement?.workoutMovementId ?? m.workoutMovementId ?? '',
          movementName: m.workoutMovement?.name ?? '',
          scoreType: m.scoreType as ScoreTypeEnum,
          distanceUnit: (m.distanceUnit as DistanceUnitEnum | '' | null) || null,
          weightUnit: (m.weightUnit as WeightUnitEnum | '' | null) || null,
          scoreSets: (m.scoreSets ?? []).map(s => ({
            setIndex: s.setIndex,
            targetReps: s.targetReps,
          })),
        })),
      };
    }),
  };
}

/**
 * Map a WorkoutRetrieve to WorkoutTemplateFormValues for prefilling the CreateWorkout form
 * (assign-from-existing flow). Like mapTemplateToFormValues, section-level sections clear the
 * section score movement FK so the clone provisions its own.
 */
export function mapWorkoutToFormValues(workout: WorkoutRetrieve): WorkoutFormValues {
  return {
    name: workout.name,
    athleteNotes: workout.athleteNotes ?? '',
    coachNotes: workout.coachNotes ?? '',
    isVisibleToAthletes: workout.isVisibleToAthletes,
    sections: (workout.sections ?? []).map((sec, i): WorkoutSectionData => {
      const isSectionLevel = inferSectionMode(sec) === 'section_level';
      const kept = isKeptBenchmarkSection(sec);
      return {
        id: sec.workoutSectionId,
        letter: getAlphabetLetterByIndex(i),
        name: sec.name,
        description: sec.workoutInstructions ?? '',
        scoreEntireSection: isSectionLevel,
        saveAsBenchmark: isSectionLevel ? kept : true,
        levelScaled: sec.levelScaled,
        levelRx: sec.levelRx,
        levelRxPlus: sec.levelRxPlus,
        movements: (sec.movements ?? []).map((m, mi) => ({
          id: m.workoutSectionMovementId,
          movementId:
            isSectionLevel && mi === 0
              ? kept
                ? m.workoutMovement.workoutMovementId
                : ''
              : m.workoutMovement.workoutMovementId,
          movementName: m.workoutMovement?.name ?? '',
          scoreType: null,
          distanceUnit: null,
          weightUnit: null,
          scoreSets: [],
        })),
      };
    }),
  };
}

/**
 * Build WorkoutCreate payload for POST workouts/ from a workout template (assign from template).
 * Scoring config (score_type, units, score_sets) is carried over from the template so the
 * resulting workout has the same scoring as its source.
 */
export function buildWorkoutWriteFromTemplate(
  template: WorkoutTemplateRetrieve,
  date: string,
  target: AssignmentTarget,
  isVisibleToAthletes: boolean,
): WorkoutCreate {
  return {
    name: template.name,
    date,
    athleteNotes: template.athleteNotes,
    coachNotes: template.coachNotes,
    isVisibleToAthletes,
    ...assignmentTargetFields(target),
    workoutTemplate: template.workoutTemplateId,
    sections: template.sections.map(section => {
      const isSectionLevel = inferSectionMode(section) === 'section_level';
      const kept = isKeptBenchmarkSection(section);
      return {
        name: section.name,
        workoutInstructions: section.workoutInstructions,
        orderIndex: section.orderIndex,
        scoreEntireSection: isSectionLevel,
        levelScaled: section.levelScaled,
        levelRx: section.levelRx,
        levelRxPlus: section.levelRxPlus,
        movements: section.movements.map(m => ({
          workoutMovement: isSectionLevel
            ? kept
              ? m.workoutMovement.workoutMovementId
              : null
            : m.workoutMovement.workoutMovementId,
          saveAsBenchmark: kept,
          orderIndex: m.orderIndex,
          scoreType: m.scoreType,
          distanceUnit: m.distanceUnit,
          weightUnit: m.weightUnit,
          scoreSets: m.scoreSets ?? [],
        })),
      };
    }),
  };
}

/**
 * Build WorkoutTemplatePreviewData for the preview modal from an assigned workout.
 * Workouts are only used as a starting point (prefill/assign), not to edit.
 */
export function buildPreviewDataFromWorkout(workout: WorkoutRetrieve): WorkoutTemplatePreviewData {
  return {
    workoutTemplateId: undefined,
    name: workout.name,
    athleteNotes: workout.athleteNotes,
    coachNotes: workout.coachNotes,
    sections: workout.sections
      .slice()
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map(section => ({
        name: section.name,
        workoutInstructions: section.workoutInstructions,
        scoreEntireSection: inferSectionMode(section) === 'section_level',
        movements: section.movements
          .slice()
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map(m => ({
            workoutMovement: {
              workoutMovementId: m.workoutMovement.workoutMovementId,
              name: m.workoutMovement?.name ?? '',
              movementType: m.workoutMovement?.movementType,
              deletedAt: m.workoutMovement?.deletedAt,
            },
            workoutSectionMovementId: m.workoutSectionMovementId,
            scoreType: m.scoreType as ScoreTypeEnum,
          })),
      })),
  };
}

/**
 * Build WorkoutTemplateWrite payload from current workout form values (used by "Save as Template"
 * in the Create/Edit Workout flow).
 */
export function buildTemplatePayloadFromFormValues(values: WorkoutTemplateFormValues): WorkoutTemplateWrite {
  return {
    workoutTemplateId: uuidv4(),
    name: values.name.trim(),
    athleteNotes: values.athleteNotes?.trim() || null,
    coachNotes: values.coachNotes?.trim() || null,
    sections: values.sections.map((section, sectionIndex) => ({
      workoutSectionTemplateId: section.id,
      name: section.name.trim(),
      workoutInstructions: section.description.trim(),
      orderIndex: sectionIndex,
      levelScaled: section.levelScaled,
      levelRx: section.levelRx,
      levelRxPlus: section.levelRxPlus,
      scoreEntireSection: section.scoreEntireSection,
      movements: (section.scoreEntireSection
        ? section.movements.filter(m => m.scoreType !== null)
        : section.movements.filter(hasMovementScoring)
      ).map((m, movementIndex) => ({
        workoutSectionMovementTemplateId: m.id,
        workoutMovement: {} as never,
        workoutMovementId: section.scoreEntireSection
          ? m.movementId || (null as unknown as string)
          : m.movementId,
        saveAsBenchmark: section.saveAsBenchmark,
        orderIndex: movementIndex,
        ...extractMovementScoringPayload(m),
      })),
    })),
  };
}

/**
 * Build WorkoutUpdate payload for PUT /workouts/:id/ from a fetched workout. Useful when
 * only one field is changing (e.g. toggling isVisibleToAthletes inline) but the endpoint
 * still requires the full payload. Pass overrides last so they win.
 */
export function buildWorkoutUpdateFromRetrieve(
  workout: WorkoutRetrieve,
  overrides: Partial<WorkoutUpdate> = {},
): WorkoutUpdate {
  return {
    name: workout.name,
    athleteNotes: workout.athleteNotes,
    coachNotes: workout.coachNotes,
    isVisibleToAthletes: workout.isVisibleToAthletes,
    sections: workout.sections.map(section => {
      const isSectionLevel = inferSectionMode(section) === 'section_level';
      const kept = isKeptBenchmarkSection(section);
      return {
        name: section.name,
        workoutInstructions: section.workoutInstructions,
        orderIndex: section.orderIndex,
        scoreEntireSection: isSectionLevel,
        levelScaled: section.levelScaled,
        levelRx: section.levelRx,
        levelRxPlus: section.levelRxPlus,
        movements: section.movements.map(m => ({
          // Re-send the existing id so the backend reuses the row (no orphaned result / new throwaway).
          workoutMovement: m.workoutMovement.workoutMovementId,
          saveAsBenchmark: kept,
          orderIndex: m.orderIndex,
          scoreType: m.scoreType,
          distanceUnit: m.distanceUnit,
          weightUnit: m.weightUnit,
          scoreSets: m.scoreSets ?? [],
        })),
      };
    }),
    ...overrides,
  };
}

/**
 * Build WorkoutCreate payload for POST /workouts/ from an assigned workout.
 * The target is taken from the program context, not from the workout.
 * Scoring config carries over from the source workout's section movements.
 */
export function buildWorkoutWriteFromWorkout(
  workout: WorkoutRetrieve,
  date: string,
  target: AssignmentTarget,
  isVisibleToAthletes: boolean,
): WorkoutCreate {
  return {
    name: workout.name,
    date,
    athleteNotes: workout.athleteNotes,
    coachNotes: workout.coachNotes,
    isVisibleToAthletes,
    ...assignmentTargetFields(target),
    workoutTemplate: null,
    sections: workout.sections.map(section => {
      const isSectionLevel = inferSectionMode(section) === 'section_level';
      const kept = isKeptBenchmarkSection(section);
      return {
        name: section.name,
        workoutInstructions: section.workoutInstructions,
        orderIndex: section.orderIndex,
        scoreEntireSection: isSectionLevel,
        levelScaled: section.levelScaled,
        levelRx: section.levelRx,
        levelRxPlus: section.levelRxPlus,
        movements: section.movements.map(m => ({
          workoutMovement: isSectionLevel
            ? kept
              ? m.workoutMovement.workoutMovementId
              : null
            : m.workoutMovement.workoutMovementId,
          saveAsBenchmark: kept,
          orderIndex: m.orderIndex,
          scoreType: m.scoreType,
          distanceUnit: m.distanceUnit,
          weightUnit: m.weightUnit,
          scoreSets: m.scoreSets ?? [],
        })),
      };
    }),
  };
}
